import { fg } from '@atlaskit/platform-feature-flags/fg';

import { Logger } from '../../common/utils';
import { getBscGlobalState } from '../../common/utils/bsc-global-state';
import type { ConsentPreference } from '../../types';

import type {
	ConsentDisplayedTextMap,
	ConsentHubRequestBody,
	ConsentHubResponse,
	ConsentPreferenceData,
} from './types';
import {
	deserializeConsents,
	getConsentTimestamp,
	serializeConsents,
	StatusCodeError,
} from './utils';

const BASE_CONSENTS_URL = '/gateway/api/consenthub/session';
const POST_CONSENTS_URL = `${BASE_CONSENTS_URL}/user`;
const GET_CONSENTS_URL = `${POST_CONSENTS_URL}/consents/cookies`;

const getConsentHubHeaders = (): Record<string, string> => ({
	'Content-Type': 'application/json',
	'X-Request-Origin': 'bsc',
	'X-Request-Origin-App': getProductForConsentHubHeader(),
});

const getProductForConsentHubHeader = (): string => {
	const product = getBscGlobalState().product;
	if (!product || product === 'all') {
		return 'unknown';
	}
	if (fg('platform_bsc_consent_hub_x_origin_app_normalized')) {
		// ConsentHub buckets anything that is not lowercase and hyphen-separated as `unknown`.
		return product.toLowerCase().replace(/\s+/g, '-');
	}
	return product;
};

export const buildConsentPreferences = (
	consentData?: ConsentHubResponse,
):
	| {
			consentToken: string;
			consentTimestamp: string | undefined;
			consentHubInaccessible?: undefined;
	  }
	| {
			consentToken: string;
			consentHubInaccessible: boolean;
			consentTimestamp?: undefined;
	  } => {
	if (consentData) {
		const serializedConsents = serializeConsents(consentData);
		const consentTimestamp = getConsentTimestamp(consentData);

		return {
			consentToken: serializedConsents,
			consentTimestamp,
		};
	}

	// Without consent data (due to server failure or unauthenticated user), set defaults illustrating that absence
	return {
		consentToken: serializeConsents({}),
		consentHubInaccessible: true,
	};
};

const fetchConsentPreferences = async (): Promise<ConsentPreferenceData> => {
	const { origin } = window.location;

	const getFromConsentHubURL = `${origin}${GET_CONSENTS_URL}`;

	try {
		const response = await fetch(getFromConsentHubURL, {
			method: 'GET',
			headers: getConsentHubHeaders(),
		});
		if (!response.ok) {
			throw new Error(
				`Failed to fetch preferences from ConsentHub with status: ${response.status}`,
			);
		}
		const consentData: ConsentHubResponse = await response.json();
		return buildConsentPreferences(consentData);
	} catch (err: any) {
		Logger.errorWithOperationalEvent({
			action: 'fetchConsentPreferencesError',
			message: err.message || '',
		});
		return buildConsentPreferences();
	}
};

const buildConsentRequestBody = (
	unpackedUserPreferences: ConsentPreference,
	displayedTextMap: ConsentDisplayedTextMap,
) => {
	const { origin, pathname } = window.location;
	const deserializedConsents = deserializeConsents(unpackedUserPreferences, displayedTextMap);

	// Type guard here so TS knows we're working with ConsentPreferenceV003
	const action =
		'CONSENT_DATA_UNAVAILABLE' in unpackedUserPreferences &&
		unpackedUserPreferences.CONSENT_DATA_UNAVAILABLE
			? 'FORM_FILL'
			: 'UPDATE_PREFERENCES';

	const consentRequestBody: ConsentHubRequestBody = {
		action, // If there are no consents, it's a first time fill, otherwise it's an update action
		consents: deserializedConsents,
		formUrl: `${origin}${pathname}`,
		locale: 'UNKNOWN',
		site: 'atlassian',
		source: 'COOKIE_PREFERENCES',
	};
	return JSON.stringify(consentRequestBody);
};

const saveConsentPreferences = async (
	unpackedUserPreferences: ConsentPreference,
	displayedTextMap: ConsentDisplayedTextMap,
): Promise<void> => {
	const saveToConsentHubURL = `${window.location.origin}${POST_CONSENTS_URL}`;

	try {
		const response = await fetch(saveToConsentHubURL, {
			method: 'POST',
			headers: getConsentHubHeaders(),
			body: buildConsentRequestBody(unpackedUserPreferences, displayedTextMap),
		});

		// Saving is an async API (we don't know if the save succeeds) so returns 202 Accepted.
		// 401 = User is unauthenticated
		// 5xx = ConsentHub is unavailable
		if (!response.ok || (response.status !== 202 && response.status !== 200)) {
			const errMsg = `Failed to save preferences to ConsentHub with status: ${response.status}`;
			const errWithStatusCode = new StatusCodeError(errMsg, response.status);
			throw errWithStatusCode;
		}
	} catch (err: any) {
		Logger.errorWithOperationalEvent({
			action: 'saveConsentPreferencesError',
			message: err.message || '',
		});
		// Re-throw to any encapsulating logic, a la `setConsentToken()`
		throw err;
	}
};

export { fetchConsentPreferences, saveConsentPreferences };
